import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT, YEAR_MONTH_DAY_STR_FORMAT } from "@/constants/datetime";
import { TOption } from "@/types/common";
import { findLabelByValue } from "@/utils/object";
import { convertNumberToZipCode } from "@/utils/input";
import { EducationLevelOptions, EForeignLanguage, TALENT_LOCATION_OPTIONS } from "@/types/enum";
import { fetchImageAsBase64, fetchSignedUrlApi, getImageExtension } from "@/api/services/global/file";
import { fetchMatchInfoApi } from "@/api/services/main/match";
import { showError } from "@/utils/error";

type TCellStyleOptions = {
  isBold?: boolean;
  isThickBorder?: boolean;
  noBorder?: boolean;
  fontSize?: number;
  fontName?: string;
  bgColor?: string;
  horizontalAlign?: "left" | "center" | "right";
  verticalAlign?: "top" | "middle" | "bottom";
  wrapText?: boolean;
};

type TMergeAndStyleCell = {
  cell: string;
  value?: string | number;
  styleOptions?: TCellStyleOptions;
};

type TImagePosition = {
  tl: { col: number; row: number };
  ext: { width: number; height: number };
  editAs: "absolute" | "oneCell" | "twoCell";
};

const BORDER_COLOR = "FF000000";
const WHITE = "FFFFFF";
const HEADER_COLOR = "EFEFEF";
const ROW_HEIGHT = 12;
const COLUMN_WIDTH = 1.9;
const FONT_SIZE = 10;

function calculateRowHeight(text: string, columnCount: number): number {
  if (!text || text.trim().length === 0) return ROW_HEIGHT;

  const charsPerColumn = Math.floor((columnCount * COLUMN_WIDTH * 7) / (FONT_SIZE * 1.5));
  const linesNeeded = Math.ceil(text.length / charsPerColumn);

  const calculatedHeight = Math.max(ROW_HEIGHT, ROW_HEIGHT * linesNeeded * 1.2);

  return Math.min(calculatedHeight, ROW_HEIGHT * 10);
}

// ============ Cell Styling ============
export class CellStyler {
  static setCellStyle(cell: ExcelJS.Cell, options?: TCellStyleOptions) {
    const {
      isBold = false,
      isThickBorder = false,
      noBorder = false,
      fontSize = 10,
      fontName = "MS PGothic",
      bgColor,
      horizontalAlign = "center",
      verticalAlign = "middle",
      wrapText = false,
    } = options || {};

    // Set border only if noBorder is false
    if (!noBorder) {
      const borderStyle = isThickBorder ? "medium" : "thin";

      cell.border = {
        top: { style: borderStyle, color: { argb: BORDER_COLOR } },
        left: { style: borderStyle, color: { argb: BORDER_COLOR } },
        bottom: { style: borderStyle, color: { argb: BORDER_COLOR } },
        right: { style: borderStyle, color: { argb: BORDER_COLOR } },
      };
    }

    cell.alignment = {
      vertical: verticalAlign,
      horizontal: horizontalAlign,
      wrapText: wrapText,
    };

    cell.font = {
      name: fontName,
      size: fontSize,
      bold: isBold,
    };

    if (bgColor) {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };
    }
  }

  static setBorderRange(sheet: ExcelJS.Worksheet, startRow: number, endRow: number, startCol: number, endCol: number) {
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const cell = sheet.getRow(row).getCell(col);
        const borders: Partial<ExcelJS.Borders> = {};

        // Use thin borders to reduce spacing when exporting to PDF
        if (row === startRow) borders.top = { style: "thin", color: { argb: BORDER_COLOR } };
        if (row === endRow) borders.bottom = { style: "thin", color: { argb: BORDER_COLOR } };
        if (col === startCol) borders.left = { style: "thin", color: { argb: BORDER_COLOR } };
        if (col === endCol) borders.right = { style: "thin", color: { argb: BORDER_COLOR } };

        if (Object.keys(borders).length > 0) {
          cell.border = { ...cell.border, ...borders };
        }
      }
    }
  }

  static columnLetterToNumber(column: string): number {
    let result = 0;
    for (let i = 0; i < column.length; i++) {
      result = result * 26 + (column.charCodeAt(i) - 64);
    }
    return result;
  }

  static setThickBorderRange(sheet: ExcelJS.Worksheet, range: string) {
    const [startCell, endCell] = range.split(":");
    const startCol = this.columnLetterToNumber(startCell.match(/[A-Z]+/)![0]);
    const startRow = parseInt(startCell.match(/\d+/)![0]);
    const endCol = this.columnLetterToNumber(endCell.match(/[A-Z]+/)![0]);
    const endRow = parseInt(endCell.match(/\d+/)![0]);

    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const cell = sheet.getRow(row).getCell(col);
        const borders: Partial<ExcelJS.Borders> = {};

        // Set thick borders only on outer edges
        if (row === startRow) borders.top = { style: "medium", color: { argb: BORDER_COLOR } };
        if (row === endRow) borders.bottom = { style: "medium", color: { argb: BORDER_COLOR } };
        if (col === startCol) borders.left = { style: "medium", color: { argb: BORDER_COLOR } };
        if (col === endCol) borders.right = { style: "medium", color: { argb: BORDER_COLOR } };

        if (Object.keys(borders).length > 0) {
          cell.border = { ...cell.border, ...borders };
        }
      }
    }
  }
}

// ============ Worksheet Manager ============
export class WorksheetManager {
  constructor(private sheet: ExcelJS.Worksheet) {}

  setColumnWidths(count: number, width: number = 1.9) {
    for (let i = 1; i <= count; i++) {
      this.sheet.getColumn(i).width = width;
    }
  }

  mergeAndStyleCell(range: string, config: TMergeAndStyleCell) {
    this.sheet.mergeCells(range);
    const cell = this.sheet.getCell(config.cell);

    if (config.value !== undefined) {
      cell.value = config.value;
    }

    if (config.styleOptions) {
      CellStyler.setCellStyle(cell, config.styleOptions);
    }

    return cell;
  }

  mergeAndStyleCellEmpty(range: string, noBorder: boolean = false, defaultValue: string = "") {
    return this.mergeAndStyleCell(range, {
      cell: range.split(":")[0],
      value: defaultValue,
      styleOptions: {
        bgColor: WHITE,
        noBorder,
        horizontalAlign: "left",
        verticalAlign: "top",
      },
    });
  }

  createLabelValuePair(
    labelRange: string,
    valueRange: string,
    labelText: string,
    valueText: string,
    labelStyle?: TCellStyleOptions,
    valueStyle?: TCellStyleOptions,
  ) {
    // Label cell
    this.mergeAndStyleCell(labelRange, {
      cell: labelRange.split(":")[0],
      value: labelText,
      styleOptions: {
        bgColor: HEADER_COLOR,
        fontSize: 10,
        ...labelStyle,
      },
    });

    // Value cell
    this.mergeAndStyleCell(valueRange, {
      cell: valueRange.split(":")[0],
      value: valueText,
      styleOptions: {
        fontSize: 16,
        ...valueStyle,
      },
    });
  }
}

// ============ Resume Builder ============
export class ResumeBuilder {
  private workbook: ExcelJS.Workbook;
  private sheet: ExcelJS.Worksheet;
  private manager: WorksheetManager;
  private currentRow: number = 1;
  private workExperienceStartRow?: number;
  private workExperienceEndRow?: number;
  private languageCertificatesStartRow?: number;
  private languageCertificatesEndRow?: number;
  private heldQualificationsStartRow?: number;
  private heldQualificationsEndRow?: number;
  private characteristicsMemoStartRow?: number;
  private characteristicsMemoEndRow?: number;
  private interviewEvaluationStartRow?: number;
  private interviewEvaluationEndRow?: number;

  constructor(
    private talentData: Partial<TTalentForm>,
    private genderOptions?: TOption[],
    private nationalityOptions?: TOption[],
    private specificSkillCertificateOptions?: TOption[],
    private languageTestOptions?: TOption[],
  ) {
    this.workbook = new ExcelJS.Workbook();
    this.sheet = this.workbook.addWorksheet("キャリアシート");
    this.manager = new WorksheetManager(this.sheet);
  }

  private buildHeader() {
    // Row 1: Empty
    this.sheet.getRow(1).height = ROW_HEIGHT;
    this.sheet.mergeCells("A1:BL1");

    // Row 2: Title
    this.sheet.getRow(2).height = 18;
    this.manager.mergeAndStyleCell("A2:BL2", {
      cell: "A2",
      value: "キャリアシート",
      styleOptions: {
        fontSize: 24,
        fontName: "MS PGothic",
        isBold: true,
        noBorder: true,
        verticalAlign: "middle",
      },
    });
    const titleCell = this.sheet.getCell("A2");
    titleCell.font = { ...titleCell.font, underline: true };

    // Row 3: Date
    this.manager.mergeAndStyleCell("A3:BL3", {
      cell: "A3",
      value: `${dayjs().format(YEAR_MONTH_DAY_STR_FORMAT)}現在`,
      styleOptions: {
        horizontalAlign: "right",
        noBorder: true,
      },
    });

    // Row 4: Section header
    this.manager.mergeAndStyleCell("A4:AN4", {
      cell: "A4",
      value: "基本情報",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
      },
    });
  }

  private async addAvatarImage(filePath: string) {
    try {
      const image = await fetchSignedUrlApi(filePath);
      if (image?.data?.signedUrl) {
        const imageUrl = image.data.signedUrl;
        const base64Image = await fetchImageAsBase64(imageUrl);
        if (!base64Image) {
          return;
        }

        const extension = getImageExtension(imageUrl) as "jpeg" | "png" | "gif";

        const imageId = this.workbook.addImage({
          base64: base64Image,
          extension: extension,
        });

        const imagePosition: TImagePosition = {
          tl: { col: 54, row: 4 }, // BC5
          ext: { width: 133, height: 213 },
          editAs: "oneCell",
        };
        this.sheet.addImage(imageId, imagePosition);
      }
    } catch (error) {
      showError(error);
    }
  }

  private getColumnName(colNumber: number): string {
    let result = "";
    while (colNumber > 0) {
      colNumber--;
      result = String.fromCharCode(65 + (colNumber % 26)) + result;
      colNumber = Math.floor(colNumber / 26);
    }
    return result;
  }

  private createRangeString(startRow: number, startCol: number, endRow: number, endCol: number): string {
    const startColName = this.getColumnName(startCol);
    const endColName = this.getColumnName(endCol);
    return `${startColName}${startRow}:${endColName}${endRow}`;
  }

  private async buildBasicInfo() {
    for (let i = 54; i <= 64; i++) {
      this.sheet.getColumn(i).width = 1.6;
    }

    // Avatar placeholder
    this.sheet.mergeCells("BB5:BB14");
    this.sheet.mergeCells("BC5:BL14");

    for (let row = 5; row <= 14; row++) {
      const rowObj = this.sheet.getRow(row);
      if (!rowObj.height) {
        rowObj.height = ROW_HEIGHT;
      }
    }

    const avatarCell = this.sheet.getCell("BC5");

    CellStyler.setCellStyle(avatarCell, {
      isThickBorder: true,
      verticalAlign: "middle",
      horizontalAlign: "center",
    });

    if (this.talentData.avatarFilePath) {
      await this.addAvatarImage(this.talentData.avatarFilePath);
    }

    // Name
    this.manager.createLabelValuePair("A5:Z5", "A6:Z8", "氏名", this.talentData.fullName ?? "", { fontSize: 10 }, { fontSize: 16 });

    // Nickname
    this.manager.createLabelValuePair("AA5:AG5", "AA6:AG8", "呼び名", this.talentData.nickName || "", { fontSize: 10 }, { fontSize: 16 });

    // Gender
    this.manager.createLabelValuePair(
      "AH5:AJ5",
      "AH6:AJ8",
      "性別",
      findLabelByValue(this.genderOptions ?? [], this.talentData.gender),
      { fontSize: 10 },
      { fontSize: 10 },
    );

    // Nationality
    this.manager.createLabelValuePair(
      "AK5:AQ5",
      "AK6:AQ8",
      "国籍",
      findLabelByValue(this.nationalityOptions ?? [], this.talentData.nationalityId),
      { fontSize: 10 },
      { fontSize: 11 },
    );

    // Date of Birth
    this.manager.mergeAndStyleCell("AR5:BA5", {
      cell: "AR5",
      value: "生年月日",
      styleOptions: { fontSize: 10, bgColor: HEADER_COLOR },
    });

    this.manager.mergeAndStyleCell("AR6:BA7", {
      cell: "AR6",
      value: this.talentData.birthday ? dayjs(this.talentData.birthday).format(YEAR_MONTH_DAY_STR_FORMAT) : "",
      styleOptions: { fontSize: 11 },
    });

    this.manager.mergeAndStyleCell("AR8:BA8", {
      cell: "AR8",
      value: `満${dayjs().diff(dayjs(this.talentData.birthday), "year")}歳`,
      styleOptions: { fontSize: 11 },
    });
  }

  private buildAddressInfo() {
    // Residence labels
    this.manager.createLabelValuePair(
      "A9:I9",
      "A10:I14",
      "居住地",
      findLabelByValue(TALENT_LOCATION_OPTIONS, this.talentData?.location),
      { fontSize: 9 },
      { fontSize: 9 },
    );

    // Current address
    this.manager.mergeAndStyleCell("J9:BA9", {
      cell: "J9",
      value: "現住所",
      styleOptions: { fontSize: 9, bgColor: HEADER_COLOR },
    });

    this.manager.mergeAndStyleCell("J10:BA11", {
      cell: "J10",
      value:
        `${this.talentData.zipCode ? convertNumberToZipCode(this.talentData.zipCode) : ""} ${this.talentData.prefecture ?? ""}${this.talentData.city ?? ""}${this.talentData.address ?? ""}`.trim(),
      styleOptions: { fontSize: 10 },
    });

    // Home country address
    this.manager.mergeAndStyleCell("J12:BA12", {
      cell: "J12",
      value: "本国における居住地",
      styleOptions: { fontSize: 9, bgColor: HEADER_COLOR },
    });

    this.manager.mergeAndStyleCell("J13:BA14", {
      cell: "J13",
      value: `${this.talentData.country ?? ""}`,
      styleOptions: { fontSize: 10, horizontalAlign: "left" },
    });

    const cityCell = this.sheet.getCell("J13");
    cityCell.border = {
      ...cityCell.border,
      left: { style: "dashed" },
    };
  }

  private getSpecifiedSkillFirstRemainingTerm(data: string | null | undefined) {
    if (!data) return "";

    const firstRemainingTerm = data.split("/");
    if (firstRemainingTerm.length !== 2) {
      return data;
    }

    const year = firstRemainingTerm[0];
    const month = firstRemainingTerm[1];

    return `${year}年${month}ヶ月`;
  }

  private buildResidenceStatusInfo() {
    this.sheet.mergeCells("W17:W19");
    this.sheet.mergeCells("BC15:BL19");

    this.manager.mergeAndStyleCell("A15:BB15", {
      cell: "A15",
      value: "",
      styleOptions: {
        noBorder: true,
        bgColor: WHITE,
      },
    });
    // Row 16: Section header
    this.manager.mergeAndStyleCell("A16:W16", {
      cell: "A16",
      value: "在留資格情報",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
      },
    });

    this.manager.mergeAndStyleCell("X16:BB16", {
      cell: "X16",
      value: "分野",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
      },
    });

    this.manager.createLabelValuePair(
      "A17:I17",
      "A18:I19",
      "在留資格",
      this.talentData?.residenceStatus?.residenceStatus ?? "",
      { fontSize: 11 },
      { fontSize: 11 },
    );
    this.manager.createLabelValuePair(
      "J17:V17",
      "J18:V19",
      "在留期限",
      this.talentData?.periodOfStay ? dayjs(this.talentData?.periodOfStay).format(DATE_FORMAT) : "",
      { fontSize: 11 },
      { fontSize: 11 },
    );

    this.manager.createLabelValuePair(
      "X17:AF17",
      "X18:AF19",
      "分野",
      this.talentData?.companySpecificIndustry?.companySpecificIndustry ?? "",
      { fontSize: 11 },
      { fontSize: 11 },
    );
    this.manager.createLabelValuePair(
      "AG17:AQ17",
      "AG18:AQ19",
      "特定技能1号起算日",
      this.talentData?.specifiedSkillFirstStartDate ? dayjs(this.talentData?.specifiedSkillFirstStartDate).format(DATE_FORMAT) : "",
      { fontSize: 11 },
      { fontSize: 11 },
    );

    const specifiedSkillFirstRemainingTerm = this.getSpecifiedSkillFirstRemainingTerm(this.talentData.specifiedSkillFirstRemainingTerm);
    this.manager.createLabelValuePair(
      "AR17:BB17",
      "AR18:BB19",
      "特定技能1号残存期間",
      specifiedSkillFirstRemainingTerm ?? "",
      { fontSize: 11 },
      { fontSize: 11 },
    );
  }

  private buildFamilyInfo() {
    this.manager.mergeAndStyleCellEmpty("A20:BL20", true);

    this.manager.mergeAndStyleCell("A21:BL21", {
      cell: "A21",
      value: "家族構成",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
      },
    });

    const familyInfo = this.talentData?.familyInformation ?? "";
    const familyInfoCell = this.manager.mergeAndStyleCellEmpty("A22:BL25", false, familyInfo);
    if (familyInfoCell) {
      familyInfoCell.alignment = { ...familyInfoCell.alignment, wrapText: true };
      const calculatedHeight = calculateRowHeight(familyInfo, 64);
      for (let i = 22; i <= 25; i++) {
        this.sheet.getRow(i).height = calculatedHeight / 4;
      }
    }
  }

  private buildEducationBackground() {
    this.manager.mergeAndStyleCellEmpty("A26:BL26", true);

    this.manager.mergeAndStyleCell("A27:BL27", {
      cell: "A27",
      value: "学歴",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
      },
    });

    const educationBackgrounds = this.talentData?.educationBackground ?? [];
    for (let i = 0; i < 5; i++) {
      const value = educationBackgrounds[i] ?? "";
      this.manager.mergeAndStyleCellEmpty(`A${28 + i}:BL${28 + i}`, false, findLabelByValue(EducationLevelOptions, String(value)));
    }
  }

  private buildWorkExperience() {
    // Empty line
    this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:BL${this.currentRow}`, true);
    this.currentRow++;

    // Section header
    this.manager.mergeAndStyleCell(`A${this.currentRow}:BL${this.currentRow}`, {
      cell: `A${this.currentRow}`,
      value: "職歴",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
      },
    });
    this.currentRow++;

    // Header row
    const headerRow = this.currentRow;

    // 開始
    this.manager.mergeAndStyleCell(`A${headerRow}:F${headerRow}`, {
      cell: `A${headerRow}`,
      value: "開始",
      styleOptions: {
        fontSize: 10,
        bgColor: HEADER_COLOR,
      },
    });

    // 終了
    this.manager.mergeAndStyleCell(`G${headerRow}:L${headerRow}`, {
      cell: `G${headerRow}`,
      value: "終了",
      styleOptions: {
        fontSize: 10,
        bgColor: HEADER_COLOR,
      },
    });

    // 企業名
    this.manager.mergeAndStyleCell(`M${headerRow}:AL${headerRow}`, {
      cell: `M${headerRow}`,
      value: "企業名",
      styleOptions: {
        fontSize: 10,
        bgColor: HEADER_COLOR,
      },
    });

    // 職種
    this.manager.mergeAndStyleCell(`AM${headerRow}:BL${headerRow}`, {
      cell: `AM${headerRow}`,
      value: "職種",
      styleOptions: {
        fontSize: 10,
        bgColor: HEADER_COLOR,
      },
    });

    this.currentRow++;

    const workExperiences = this.talentData?.workExperiences ?? [];
    const maxRows = Math.max(workExperiences.length, 4);

    this.workExperienceStartRow = headerRow;

    for (let i = 0; i < maxRows; i++) {
      const workExperience = workExperiences[i];

      // Start Date
      const startDate = workExperience?.startDate ? dayjs(workExperience.startDate).format(DATE_FORMAT) : "";
      this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:F${this.currentRow}`, false, startDate);

      // End Date
      const endDate = workExperience?.endDate ? dayjs(workExperience.endDate).format(DATE_FORMAT) : "";
      this.manager.mergeAndStyleCellEmpty(`G${this.currentRow}:L${this.currentRow}`, false, endDate);

      // Company Name
      const companyName = workExperience?.companyName ?? "";
      const companyNameCell = this.manager.mergeAndStyleCellEmpty(`M${this.currentRow}:AL${this.currentRow}`, false, companyName);
      if (companyNameCell) {
        companyNameCell.alignment = { ...companyNameCell.alignment, wrapText: true };
        const calculatedHeight = calculateRowHeight(companyName, 30); // M to AL = 30 columns
        this.sheet.getRow(this.currentRow).height = Math.max(this.sheet.getRow(this.currentRow).height || ROW_HEIGHT, calculatedHeight);
      }

      const jobType = workExperience?.jobType ?? "";
      const jobTypeCell = this.manager.mergeAndStyleCellEmpty(`AM${this.currentRow}:BL${this.currentRow}`, false, jobType);
      if (jobTypeCell) {
        jobTypeCell.alignment = { ...jobTypeCell.alignment, wrapText: true };
        const calculatedHeight = calculateRowHeight(jobType, 30); // AM to BL = 30 columns
        this.sheet.getRow(this.currentRow).height = Math.max(this.sheet.getRow(this.currentRow).height || ROW_HEIGHT, calculatedHeight);
      }

      this.currentRow++;
    }

    this.workExperienceEndRow = this.currentRow - 1;

    // Add border
    CellStyler.setBorderRange(this.sheet, headerRow, this.currentRow - 1, 1, 64);
  }

  private buildLanguageCertificates() {
    // Empty line
    this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:BL${this.currentRow}`, true);
    this.currentRow++;

    this.manager.mergeAndStyleCell(`A${this.currentRow}:BL${this.currentRow}`, {
      cell: `A${this.currentRow}`,
      value: "語学",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
      },
    });
    this.currentRow++;

    const startRow = this.currentRow;
    this.languageCertificatesStartRow = startRow;

    // Row 1: 日本語
    this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:F${this.currentRow}`, false, "日本語");
    this.manager.mergeAndStyleCellEmpty(
      `G${this.currentRow}:R${this.currentRow}`,
      false,
      findLabelByValue(this.languageTestOptions ?? [], this.talentData.languageCertificates?.[0]?.languageTestId ?? ""),
    );
    this.manager.mergeAndStyleCellEmpty(
      `S${this.currentRow}:AC${this.currentRow}`,
      false,
      this.talentData.languageCertificates?.[0]?.languageTestLevel?.level ?? "",
    );
    this.manager.mergeAndStyleCellEmpty(`AD${this.currentRow}:BL${this.currentRow}`, false, this.talentData.languageTestOther ?? "");
    this.currentRow++;

    const languageMap = Object.fromEntries((this.talentData.languageSkills ?? []).map((item) => [item.language, item.content ?? ""])) as Partial<
      Record<EForeignLanguage, string>
    >;

    const languageRows = [
      { label: "英語", language: EForeignLanguage.ENGLISH },
      { label: "語学", language: EForeignLanguage.CHINESE },
      { label: "それ以外", language: EForeignLanguage.OTHER },
    ];

    languageRows.forEach(({ label, language }) => {
      this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:F${this.currentRow}`, false, label);
      const languageContent = languageMap[language] ?? "";
      const languageCell = this.manager.mergeAndStyleCellEmpty(`G${this.currentRow}:R${this.currentRow}`, false, languageContent);
      if (languageCell) {
        languageCell.alignment = { ...languageCell.alignment, wrapText: true };
        const calculatedHeight = calculateRowHeight(languageContent, 12); // G to R = 12 columns
        this.sheet.getRow(this.currentRow).height = Math.max(this.sheet.getRow(this.currentRow).height || ROW_HEIGHT, calculatedHeight);
      }
      this.manager.mergeAndStyleCellEmpty(`S${this.currentRow}:AC${this.currentRow}`);
      this.manager.mergeAndStyleCellEmpty(`AD${this.currentRow}:BL${this.currentRow}`);
      this.currentRow++;
    });

    this.languageCertificatesEndRow = this.currentRow - 1;

    // Add border
    CellStyler.setBorderRange(this.sheet, startRow, this.currentRow - 1, 1, 64);
  }

  private buildSpecificSkillCertificate() {
    // Empty line
    this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:BL${this.currentRow}`, true);
    this.currentRow++;

    this.manager.mergeAndStyleCell(`A${this.currentRow}:BL${this.currentRow}`, {
      cell: `A${this.currentRow}`,
      value: "特定技能合格証",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
      },
    });
    this.currentRow++;

    const specificSkillCertificates = this.talentData.specificSkillCertificates ?? [];

    const lengthSpecificSkillCertificates = Math.max(specificSkillCertificates.length, 3);
    const startRow = this.currentRow;

    for (let i = 0; i < lengthSpecificSkillCertificates; i++) {
      const certificate = findLabelByValue(this.specificSkillCertificateOptions ?? [], String(specificSkillCertificates[i]));
      this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:F${this.currentRow}`, false, `証明書名`);
      this.manager.mergeAndStyleCellEmpty(`G${this.currentRow}:BL${this.currentRow}`, false, certificate);
      this.currentRow++;
    }

    CellStyler.setBorderRange(this.sheet, startRow, this.currentRow - 1, 1, 64);
  }

  private buildHeldQualifications() {
    // Empty line
    this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:BL${this.currentRow}`, true);
    this.currentRow++;

    this.manager.mergeAndStyleCell(`A${this.currentRow}:BL${this.currentRow}`, {
      cell: `A${this.currentRow}`,
      value: "保有資格",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
      },
    });
    this.currentRow++;

    // Auto increase rows
    const heldQualifications = this.talentData?.heldQualifications ?? [];
    const rowCount = Math.max(heldQualifications.length, 3);
    const startRow = this.currentRow;

    this.heldQualificationsStartRow = startRow;

    for (let i = 0; i < rowCount; i++) {
      const value = heldQualifications[i] ?? "";
      const qualificationCell = this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:BL${this.currentRow}`, false, value);
      if (qualificationCell) {
        qualificationCell.alignment = { ...qualificationCell.alignment, wrapText: true };
        const calculatedHeight = calculateRowHeight(value, 64); // A to BL = 64 columns
        this.sheet.getRow(this.currentRow).height = Math.max(this.sheet.getRow(this.currentRow).height || ROW_HEIGHT, calculatedHeight);
      }
      this.currentRow++;
    }

    this.heldQualificationsEndRow = this.currentRow - 1;

    // Add border
    CellStyler.setBorderRange(this.sheet, startRow, this.currentRow - 1, 1, 64);
  }

  private buildCharacteristicsMemo() {
    // Empty line
    this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:BL${this.currentRow}`, true);
    this.currentRow++;

    this.manager.mergeAndStyleCell(`A${this.currentRow}:BL${this.currentRow}`, {
      cell: `A${this.currentRow}`,
      value: "特性メモ",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
      },
    });
    this.currentRow++;

    const startRow = this.currentRow;
    const endRow = this.currentRow + 3; // 4 rows for memo

    this.characteristicsMemoStartRow = startRow;
    this.characteristicsMemoEndRow = endRow;

    const memoText = this.talentData?.memo ?? "";
    const memoCell = this.manager.mergeAndStyleCellEmpty(`A${startRow}:BL${endRow}`, false, memoText);
    if (memoCell) {
      memoCell.alignment = { ...memoCell.alignment, wrapText: true };
      // Calculate height for memo (4 rows merged = 64 columns)
      const calculatedHeight = calculateRowHeight(memoText, 64);
      const heightPerRow = Math.max(ROW_HEIGHT, calculatedHeight / 4);
      for (let i = startRow; i <= endRow; i++) {
        this.sheet.getRow(i).height = heightPerRow;
      }
    } else {
      for (let i = startRow; i <= endRow; i++) {
        this.sheet.getRow(i).height = ROW_HEIGHT;
      }
    }

    this.currentRow = endRow + 1;

    CellStyler.setBorderRange(this.sheet, startRow, endRow, 1, 64);
  }

  private buildInterviewEvaluation() {
    // Empty line
    this.manager.mergeAndStyleCellEmpty(`A${this.currentRow}:BL${this.currentRow}`, true);
    this.currentRow++;

    this.manager.mergeAndStyleCell(`A${this.currentRow}:BL${this.currentRow}`, {
      cell: `A${this.currentRow}`,
      value: "面談評価",
      styleOptions: {
        fontSize: 11,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
      },
    });
    this.currentRow++;

    const startRow = this.currentRow;
    const endRow = this.currentRow + 3; // 4 rows for evaluation

    this.interviewEvaluationStartRow = startRow;
    this.interviewEvaluationEndRow = endRow;

    const evaluationText = this.talentData?.interviewEvaluation ?? "";
    const evaluationCell = this.manager.mergeAndStyleCellEmpty(`A${startRow}:BL${endRow}`, false, evaluationText);
    if (evaluationCell) {
      evaluationCell.alignment = { ...evaluationCell.alignment, wrapText: true };
      // Calculate height for evaluation (4 rows merged = 64 columns)
      const calculatedHeight = calculateRowHeight(evaluationText, 64);
      const heightPerRow = Math.max(ROW_HEIGHT, calculatedHeight / 4);
      for (let i = startRow; i <= endRow; i++) {
        this.sheet.getRow(i).height = heightPerRow;
      }
    } else {
      for (let i = startRow; i <= endRow; i++) {
        this.sheet.getRow(i).height = ROW_HEIGHT;
      }
    }

    this.currentRow = endRow + 1;

    CellStyler.setBorderRange(this.sheet, startRow, endRow, 1, 64);
  }

  private async buildFooter() {
    const matchInfo = await fetchMatchInfoApi();

    let matchName = "";
    if (matchInfo?.data?.matchName) {
      matchName = matchInfo?.data?.matchName;
    }

    this.currentRow++; // Extra space
    const footerStartRow = this.currentRow;

    this.manager.mergeAndStyleCell(`A${this.currentRow}:X${this.currentRow + 1}`, {
      cell: `A${this.currentRow}`,
      value: `【${matchName}】いろはな株式会社`,
      styleOptions: {
        fontSize: 14,
        horizontalAlign: "left",
        noBorder: true,
        bgColor: WHITE,
        isBold: true,
      },
    });

    this.manager.mergeAndStyleCellEmpty(`AR${this.currentRow}:BL${this.currentRow}`, true);

    this.manager.mergeAndStyleCell(`AR${this.currentRow + 1}:BL${this.currentRow + 1}`, {
      cell: `AR${this.currentRow + 1}`,
      value: "上記内容は事実と相違ございません。",
      styleOptions: {
        fontSize: 9,
        horizontalAlign: "right",
        noBorder: true,
        bgColor: WHITE,
      },
    });

    this.currentRow = footerStartRow + 2;
  }

  async build() {
    this.manager.setColumnWidths(64);

    // ========== FIXED SECTIONS (Rows 1-32) ==========
    this.buildHeader(); // Rows 1-4
    await this.buildBasicInfo(); // Rows 5-14
    this.buildAddressInfo(); // Rows 9-14 (merged with basic info)
    this.buildResidenceStatusInfo(); // Rows 15-19
    this.buildFamilyInfo(); // Rows 20-25
    this.buildEducationBackground(); // Rows 26-32

    // Add borders for fixed sections
    CellStyler.setBorderRange(this.sheet, 5, 14, 1, 53); // Basic info + Address
    CellStyler.setBorderRange(this.sheet, 17, 19, 1, 22); // Residence status left
    CellStyler.setBorderRange(this.sheet, 17, 19, 24, 54); // Residence status right
    CellStyler.setBorderRange(this.sheet, 22, 25, 1, 64); // Family info
    CellStyler.setBorderRange(this.sheet, 28, 32, 1, 64); // Education

    // ========== DYNAMIC SECTIONS  ==========
    this.currentRow = 33;

    this.buildWorkExperience();
    this.buildLanguageCertificates();
    this.buildSpecificSkillCertificate();
    this.buildHeldQualifications();
    this.buildCharacteristicsMemo();
    this.buildInterviewEvaluation();
    await this.buildFooter();

    // Add thick borders on outer edges for specified ranges
    CellStyler.setThickBorderRange(this.sheet, "A5:BA14");
    CellStyler.setThickBorderRange(this.sheet, "BC5:BL14");
    CellStyler.setThickBorderRange(this.sheet, "A17:V19");
    CellStyler.setThickBorderRange(this.sheet, "X17:BB19");
    CellStyler.setThickBorderRange(this.sheet, "A22:BL25");
    CellStyler.setThickBorderRange(this.sheet, "A28:BL32");

    // Dynamic ranges based on actual data
    if (this.workExperienceStartRow && this.workExperienceEndRow) {
      const workExperienceRange = this.createRangeString(this.workExperienceStartRow, 1, this.workExperienceEndRow, 64);
      CellStyler.setThickBorderRange(this.sheet, workExperienceRange);
    }

    if (this.languageCertificatesStartRow && this.languageCertificatesEndRow) {
      const languageRange = this.createRangeString(this.languageCertificatesStartRow, 1, this.languageCertificatesEndRow, 64);
      CellStyler.setThickBorderRange(this.sheet, languageRange);
    }

    if (this.heldQualificationsStartRow && this.heldQualificationsEndRow) {
      const heldQualificationsRange = this.createRangeString(this.heldQualificationsStartRow, 1, this.heldQualificationsEndRow, 64);
      CellStyler.setThickBorderRange(this.sheet, heldQualificationsRange);
    }

    if (this.characteristicsMemoStartRow && this.characteristicsMemoEndRow) {
      const characteristicsMemoRange = this.createRangeString(this.characteristicsMemoStartRow, 1, this.characteristicsMemoEndRow, 64);
      CellStyler.setThickBorderRange(this.sheet, characteristicsMemoRange);
    }

    if (this.interviewEvaluationStartRow && this.interviewEvaluationEndRow) {
      const interviewEvaluationRange = this.createRangeString(this.interviewEvaluationStartRow, 1, this.interviewEvaluationEndRow, 64);
      CellStyler.setThickBorderRange(this.sheet, interviewEvaluationRange);
    }

    // Configure page setup for PDF export
    this.sheet.pageSetup = {
      paperSize: 9, // A4
      orientation: "portrait",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: {
        left: 0.2,
        right: 0.2,
        top: 0.2,
        bottom: 0.2,
        header: 0,
        footer: 0,
      },
      printArea: `A1:BL${this.currentRow}`,
    };

    for (let i = 1; i <= this.currentRow; i++) {
      const row = this.sheet.getRow(i);
      if (row.height === undefined || row.height === null) {
        row.height = ROW_HEIGHT;
      }
    }

    // Set column widths for BM to CA
    for (let i = 65; i <= 79; i++) {
      this.sheet.getColumn(i).width = 1.9;
    }

    return this.workbook;
  }
}

// ============ Main Export Function ============
type TGenerateResumeExcelProps = {
  talentData: Partial<TTalentForm>;
  genderOptions?: TOption[];
  nationalityOptions?: TOption[];
  specificSkillCertificateOptions?: TOption[];
  languageTestOptions?: TOption[];
};

export const generateResumeExcel = async ({
  talentData,
  genderOptions,
  nationalityOptions,
  specificSkillCertificateOptions,
  languageTestOptions,
}: TGenerateResumeExcelProps) => {
  const builder = new ResumeBuilder(talentData, genderOptions, nationalityOptions, specificSkillCertificateOptions, languageTestOptions);
  const workbook = await builder.build();

  const fileName = `${talentData.fullName}の履歴書.xlsx`;

  // Download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fileName);
};
